import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import axios from "axios";
import { api, BACKEND_URL, C, errorMessage } from "../lib/api";
import { useClubRoomsOn } from "../hooks/useClubRoomsOn";
import { toast } from "sonner";
import { getAsset } from "../assets/assetManifest";
import { useAuth } from "../context/AuthContext";
import { getClub } from "../data/clubs";
import { ClubTicker, ClubTopBar } from "../components/club/ClubChrome";
import { GamesPanel, NoticePanel, ClubScreen, WeekPanel, AdSlot } from "../components/club/ClubBoards";
import { ClubGate } from "../components/club/ClubGate";
import { JoinButton } from "../components/JoinDialog";

// Club rooms are for members: a guest sees the door, not the room.
const GuestClubDoor = ({ club }) => (
  <div data-testid="club-guest-door" className="min-h-screen flex items-center justify-center p-6 relative"
    style={{ backgroundImage: `url(${club.lounge || club.lobby})`, backgroundSize: "cover", backgroundPosition: "center" }}>
    <div className="absolute inset-0 bg-black/70" />
    <div className="relative gold-card rounded-2xl p-7 max-w-md w-full flex flex-col items-center gap-4 text-center">
      {club.logo && <img src={club.logo} alt={club.name} className="h-16 object-contain" />}
      <div className="font-display text-lg text-amber-50 tracking-wider">MEMBERS ONLY</div>
      <p className="text-sm text-slate-300">Club rooms are for Players Room members. Join free — you keep every chip you have and get 50,000 more.</p>
      <JoinButton testId="club-guest-join" label="JOIN & CLAIM 50,000 CHIPS" reason="Club rooms are for members." />
      <a href="/lobby" data-testid="club-guest-back" className="text-xs text-slate-400 hover:text-amber-300 underline underline-offset-4">Back to the lobby</a>
    </div>
  </div>
);

// A club's lobby inside Players Room: ONE screen, their room, their colours. Same architecture as the
// Players Room lobby — a background you are standing in, signboards on the walls, games in the middle.
// Everything is driven by the club schema (data/clubs.js) plus what the club edits in their dashboard.
export default function ClubRoomPage() {
  const { slug } = useParams();
  const club = getClub(slug);
  const { user } = useAuth();
  const roomsOn = useClubRoomsOn();
  const [live, setLive] = useState(null);
  const [mtts, setMtts] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!club) return;
    axios.get(`${BACKEND_URL}/api/clubs/${club.slug}`)
      .then((r) => setLive(r.data))
      .catch(() => setLive({ events: [] }));
  }, [club]);

  // Their tournaments as the lobby sees them: who is registered, who has a seat, when rego opens.
  const loadMtts = useCallback(() => {
    if (!user) return;
    api.get("/api/mtt")
      .then((r) => setMtts(Object.fromEntries(r.data.map((row) => [row.event_id, row]))))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    loadMtts();
    const id = setInterval(loadMtts, 10000);
    return () => clearInterval(id);
  }, [loadMtts]);

  const [rings, setRings] = useState([]);
  useEffect(() => {   // this club's ring games (they're listed here, not in the main lobby)
    if (!user || !club) return;
    const load = () => api.get(C.ApiRoutes.TABLES).then((r) => setRings(r.data.filter((t) => t.club === club.slug && t.format === "CASH"))).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [user, club]);

  const startNow = async (eventId) => {
    setBusy(eventId);
    try {
      await api.post(`/api/mtt/${eventId}/start-demand`);
      toast.success("Started — cards in 3 minutes. Take your seat and invite your friends.");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy("");
      loadMtts();
    }
  };

  const register = async (eventId) => {
    setBusy(eventId);
    try {
      await api.post(`/api/mtt/${eventId}/register`);
      toast.success("You're in — your seat opens when the tournament starts.");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy("");
      loadMtts();
    }
  };

  if (!club) return <Navigate to="/" replace />;
  // demo mode: a room switched off in admin is closed to players (staff can still look in)
  if (!C.STAFF_ROLES.includes(user?.role)) {
    if (!roomsOn) return null; // nothing shows until the switch is known: no flash of a closed room
    if (!roomsOn.includes(club.slug)) return <Navigate to="/" replace />;
  }

  if (user?.guest) return <GuestClubDoor club={club} />;

  const t = club.theme;
  const signedIn = !!user;
  const ticker = (live?.ticker?.length ? live.ticker : club.ticker) || [];
  const venues = (live?.venues?.length ? live.venues : club.venues) || [];
  const notices = (live?.notices?.length ? live.notices : club.notices) || [];
  // tournaments taking players right now go to the top of "Playing now"
  const liveRank = (ev) => (["RUNNING", "REGISTERING"].includes(mtts[ev.event_id]?.instance?.status) ? 0 : 1);
  const events = [...(live?.events || [])].sort((x, y) => liveRank(x) - liveRank(y));
  const allGames = (live?.games?.length ? live.games : club.games) || [];
  // a game the club has set up is also a real tournament — show it once, as the tournament
  const eventNames = new Set(events.map((e) => (e.name || "").toLowerCase()));
  const games = allGames.filter((g) => !eventNames.has((g.name || "").toLowerCase()));

  return (
    <div className="club-room h-screen flex flex-col overflow-hidden relative"
      data-testid={`club-page-${club.slug}`}
      style={{
        "--c-ground": t.ground, "--c-surface": t.surface, "--c-line": t.line, "--c-ink": t.ink,
        "--c-ink-soft": t.inkSoft, "--c-accent": t.accent, "--c-accent-deep": t.accentDeep,
        "--c-on-accent": t.onAccent, "--c-display": t.display, "--c-body": t.body,
        "--c-tracking": t.displayTracking, "--c-radius": t.radius,
        backgroundColor: t.ground,
      }}>
      {/* the room you are standing in: the club's lobby, thrown out of focus so the signage sits in front of it */}
      <div aria-hidden className="absolute inset-0"
        style={{ backgroundImage: `url(${club.lounge || club.lobby})`, backgroundSize: "cover", backgroundPosition: "center",
          filter: "blur(7px) saturate(1.08)", transform: "scale(1.06)" }} />
      <div aria-hidden className="absolute inset-0"
        style={{ background: `linear-gradient(180deg, ${t.ground}cc 0%, ${t.ground}4d 42%, ${t.ground}e6 100%)` }} />
      <div aria-hidden data-testid="club-carpet" className="pointer-events-none absolute inset-x-0 bottom-0 h-[42vh]"
        style={{ perspective: "700px", perspectiveOrigin: "50% 0%", maskImage: "linear-gradient(to bottom, transparent 0%, #000 55%)", WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, #000 55%)" }}>
        <div className="absolute left-[-40%] right-[-40%] bottom-[-10%] h-[160%]"
          style={{ backgroundImage: `url(${getAsset("tex_carpet").path})`, backgroundSize: "420px 420px", transform: "rotateX(62deg)", transformOrigin: "50% 100%", opacity: 0.5, filter: "brightness(0.7) saturate(0.6)" }} />
      </div>

      <div className="relative flex flex-col h-full min-h-0">
        <ClubTopBar club={club} backTo={signedIn ? "/lobby" : "/"} />
        <ClubTicker lines={ticker} />

        <main className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-4 px-4 md:px-8 py-4">
          <section className="lg:col-span-3 flex flex-col min-h-0 gap-5 club-rise">
            <WeekPanel venues={venues} region={club.contact.region} />
            <ClubScreen label="The room" testId="club-screen-room" origin="bottom left"
              heightClass="h-[22vh]" interval={8000}
              slides={(live?.screens?.photos?.length ? live.screens.photos.map((p) => `${BACKEND_URL}${p.url}`) : club.gallery || [])
                .map((src) => ({ src, fit: "cover", label: club.name }))} />
          </section>

          <section className="lg:col-span-6 flex flex-col min-h-0 gap-4 club-rise" style={{ animationDelay: "90ms" }}>
            <div className="club-panel px-5 py-4 shrink-0 flex items-center gap-4" data-testid="club-marquee">
              <img src={club.logo} alt={club.name} className={`${club.logoClass || "h-10 md:h-14"} object-contain shrink-0`} data-testid="club-hero-logo" />
              <div className="flex flex-col min-w-0">
                <span className="club-display text-lg md:text-2xl uppercase leading-none club-neon" data-testid="club-name">{club.headline}</span>
                <span className="text-[11px] club-soft truncate">{live?.club?.blurb || club.eyebrow}</span>
              </div>
            </div>
            <div className="relative shrink-0 flex flex-col">
              <GamesPanel events={events} games={games} signedIn={signedIn} short={club.short}
                mtts={mtts} onRegister={register} onStart={startNow} rings={rings} busy={busy} />
              {!signedIn && <ClubGate club={club} />}
            </div>
            <AdSlot club={club} />
          </section>

          <section className="lg:col-span-3 flex flex-col min-h-0 gap-5 club-rise" style={{ animationDelay: "180ms" }}>
            <ClubScreen label="What's on" testId="club-screen-posters" origin="top right" heightClass="h-[26vh]"
              slides={live?.screens?.posters?.length
                ? live.screens.posters.map((p) => ({ src: `${BACKEND_URL}${p.url}`, label: p.label }))
                : (club.posters || []).filter((p) => p.src)} />
            <NoticePanel notices={notices} />
          </section>
        </main>

        <footer className="shrink-0 px-4 md:px-8 py-2 flex items-center justify-between gap-4 text-[10px] club-soft"
          data-testid="club-footer">
          <span>{[club.name, club.contact.email, club.contact.phone].filter(Boolean).join(" · ")}</span>
          <span>Play money only · Powered by Players Room</span>
        </footer>
      </div>
    </div>
  );
}
